/**
 * Which enquiries this browser has already been shown the inside of.
 *
 * The list marks an enquiry nobody has opened yet with a "New" tag, and the
 * rail carries how many of those there are. Both are read from here, and both
 * stop being true for a record the moment its detail screen loads — see
 * `markEnquiryOpened`, which is what `EnquiryDetailPage` calls.
 *
 * The machinery underneath is `createOpenedRecordsStore`, which clients keep
 * one of too — see `openedClients`. What is particular to enquiries is only
 * the storage key and the names below.
 */

import { createOpenedRecordsStore } from "../../lib/openedRecords";
import type { EnquiryStatus } from "./types/enquiries.types";

/** Single key, holding the ids in the order they were opened. */
const STORAGE_KEY = "naurist:opened-enquiries";

const store = createOpenedRecordsStore(STORAGE_KEY);

/** Subscribes to every change made here, and returns the unsubscribe. */
export const subscribeToOpenedEnquiries = store.subscribe;

/**
 * The ids opened on this device, as a set to test rows against. Read from
 * storage on the first call rather than in an effect, so a reloaded list draws
 * its tags in the first paint instead of flashing them on and off.
 */
export const getOpenedEnquiries = store.get;

/**
 * Whether an enquiry still counts as new: nobody here has opened it, and it
 * is still at the stage every enquiry starts in.
 *
 * The second half is what keeps a wiped browser honest. Opened-ness is only
 * this device's memory — clear its storage, or sign in somewhere else, and
 * every enquiry looks unopened again. But a stage past "Recently Added" was
 * chosen by somebody, so a row wearing one has been handled whatever this
 * browser remembers, and tagging it "New" would be wrong. Only an enquiry
 * nobody has moved can honestly be new, so only there is the store consulted.
 *
 * A read flag on the wire would make this exact; until the backend carries
 * one, the stage is the closest thing to it the response holds.
 */
export const isNewEnquiry = (
  enquiry: { id: string; status: EnquiryStatus },
  opened: ReadonlySet<string>
): boolean => enquiry.status === "Recently Added" && !opened.has(enquiry.id);

/**
 * Marks one enquiry as read, which is what takes its "New" tag away and its
 * one off the rail's count.
 *
 * A no-op for an id already held, so the detail screen may call it on every
 * render without waking a single listener after the first.
 */
export const markEnquiryOpened = store.mark;

/**
 * Forgets everything, in memory and in storage.
 *
 * Test-only, and for the same reason `resetEnquiriesStore` exists: the store
 * is a module singleton over a `localStorage` key, so without this an enquiry
 * one test opens stays opened for the next one.
 */
export const resetOpenedEnquiries = store.reset;
